import { useEffect, useState } from 'react';

/**
 * The mobile safe-area insets RESOLVED TO PIXELS, for the consumers that cannot
 * take a CSS value.
 *
 * `globals.css` spells `env(safe-area-inset-*)` exactly once, as the `--safe-*`
 * custom properties, and every layout consumer reads those through a class or a
 * `calc()` (Tailwind's `safe-*` spacing tokens, the `SAFE_AREA_*` constants in
 * `shared/styles/layout.constants.ts`). This hook is the one seam for a consumer
 * that needs a NUMBER instead — Floating UI's `collisionPadding`, which Radix's
 * floating primitives hand to a JS collision solver and which therefore cannot
 * take `var(--safe-bottom)` or `env(safe-area-inset-bottom)` in any spelling.
 *
 * It still reads the same `--safe-*` variables rather than calling `env()` again,
 * so this stays a CONSUMER of that single spelling and never a second one — and
 * so overriding the variables on `document.documentElement` moves this value too,
 * which is what makes the fix verifiable in a desktop browser at all
 * (docs/design-system/ui-mobile-responsive.md § 1b).
 *
 * Every inset is 0 on the web and on desktop, so it is safe to call
 * unconditionally.
 */
export interface SafeAreaInsets {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

const ZERO_INSETS: SafeAreaInsets = { top: 0, right: 0, bottom: 0, left: 0 };

/**
 * A `--safe-*` custom property resolves to a `px` length, and to the empty string
 * where the variable is not declared at all — which is the same fact as a zero
 * inset, since `globals.css` declares each one with a `0px` fallback.
 */
function readInset(styles: CSSStyleDeclaration, name: string): number {
  const parsed = Number.parseFloat(styles.getPropertyValue(name));
  return Number.isFinite(parsed) ? parsed : 0;
}

function readSafeAreaInsets(): SafeAreaInsets {
  const styles = getComputedStyle(document.documentElement);
  return {
    top: readInset(styles, '--safe-top'),
    right: readInset(styles, '--safe-right'),
    bottom: readInset(styles, '--safe-bottom'),
    left: readInset(styles, '--safe-left'),
  };
}

function sameInsets(a: SafeAreaInsets, b: SafeAreaInsets): boolean {
  return (
    a.top === b.top &&
    a.right === b.right &&
    a.bottom === b.bottom &&
    a.left === b.left
  );
}

/**
 * The current safe-area insets in pixels, re-read on resize and on orientation
 * change.
 *
 * Re-reading is not a formality: the Android navigation bar changes height
 * between gesture and three-button navigation, and both bars swap edges when the
 * device rotates, so a value read once at mount is wrong for the rest of the
 * session. The state is replaced only when a number actually moved, so a resize
 * storm re-renders nothing.
 */
export function useSafeAreaInsets(): SafeAreaInsets {
  const [insets, setInsets] = useState<SafeAreaInsets>(readSafeAreaInsets);

  useEffect(() => {
    const sync = () => {
      const next = readSafeAreaInsets();
      setInsets((current) => (sameInsets(current, next) ? current : next));
    };

    // Mount reads the insets a second time: the first read happens during
    // render, which on the very first paint can precede the WebView reporting
    // its real insets.
    sync();

    window.addEventListener('resize', sync);
    window.addEventListener('orientationchange', sync);
    return () => {
      window.removeEventListener('resize', sync);
      window.removeEventListener('orientationchange', sync);
    };
  }, []);

  return insets;
}

/**
 * Floating UI's `collisionPadding`: a number applied to all four sides, or a
 * per-side object.
 */
export type CollisionPadding = number | Partial<SafeAreaInsets>;

/**
 * Merge a caller's `collisionPadding` with the safe-area insets, taking the
 * LARGER of the two per side.
 *
 * Larger rather than sum, because the two express the same thing — how much of
 * each edge the panel must stay clear of. A caller's 8px of air and a 48dp
 * navigation bar are not additive; the bar simply subsumes the air.
 *
 * Exported for its own unit test: this is where the arithmetic lives, and the
 * primitives below only wire it up.
 */
export function mergeSafeAreaCollisionPadding(
  collisionPadding: CollisionPadding,
  insets: SafeAreaInsets
): SafeAreaInsets {
  const base =
    typeof collisionPadding === 'number'
      ? {
          top: collisionPadding,
          right: collisionPadding,
          bottom: collisionPadding,
          left: collisionPadding,
        }
      : { ...ZERO_INSETS, ...collisionPadding };

  return {
    top: Math.max(base.top, insets.top),
    right: Math.max(base.right, insets.right),
    bottom: Math.max(base.bottom, insets.bottom),
    left: Math.max(base.left, insets.left),
  };
}

/**
 * The `collisionPadding` a Radix floating primitive (`Popover`, `DropdownMenu`,
 * `Tooltip`, and the `Select` built on `Popover`) must pass so that a panel
 * opened near the bottom of an edge-to-edge Android screen keeps its last item
 * clear of the navigation bar.
 *
 * Those primitives position against the VIEWPORT, which on that shell runs behind
 * the system bars — so Floating UI's collision detection treats the strip behind
 * the navigation bar as usable space and settles a menu with its final item where
 * a finger cannot reach it. `html`'s own safe-area padding cannot help: the
 * containing block of the portal is the viewport, not the padded `html` box, and
 * `collisionPadding` is a number consumed in JS rather than a CSS length.
 *
 * The primitives carry this themselves, so a CALLER adds nothing — the same
 * contract the touch-target floor and the shells' safe-area padding already have
 * (docs/design-system/ui-mobile-responsive.md § 1a and § 1b). A caller that does
 * pass a `collisionPadding` keeps it as a FLOOR: every inset is 0 on the web, so
 * the merged value is byte-identical to what it asked for there.
 */
export function useSafeCollisionPadding(
  collisionPadding: CollisionPadding = 0
): SafeAreaInsets {
  const insets = useSafeAreaInsets();
  return mergeSafeAreaCollisionPadding(collisionPadding, insets);
}
